import { useEffect, useId, useMemo, useRef, useState } from 'react';

import type { DestinationDto, DestinationFeature, DestinationNetwork } from '@mpr/shared';

import { useI18n } from '../i18n/LanguageProvider.js';
import type { TranslationKey } from '../i18n/translations.js';
import { useDestinations, type DestinationEntry } from '../lib/destinations.js';
import { networkLabel, withChannel } from '../lib/channels.js';

/** More entries than this and a search box earns its room. */
const SEARCH_FROM = 8;

function useLabels() {
  const { t } = useI18n();
  const network = (value: DestinationNetwork, fallback?: string) => networkLabel(t, value, fallback);
  const reason = (entry: DestinationEntry) =>
    entry.cap.reason ? t(`dest.reason.${entry.cap.reason}` as TranslationKey) : '';
  const kind = (dest: DestinationDto) =>
    dest.kind === 'PAGE' ? '' : t(`social.account.${dest.kind}` as TranslationKey);
  const unsupported = (networks: DestinationNetwork[]) =>
    t('dest.unsupported', { networks: networks.map((value) => network(value)).join('، ') });
  return { t, network, reason, kind, unsupported };
}

function matches(dest: DestinationDto, query: string): boolean {
  return dest.name.toLowerCase().includes(query.trim().toLowerCase());
}

/**
 * Pick one destination, or none ("all"), to filter a list by.
 *
 * One component for every screen, so the same Page is offered the same way
 * everywhere and a new network needs no change here. On a phone it opens as a
 * sheet from the bottom edge instead of a popover that would be clipped.
 */
export function DestinationFilter(props: {
  feature: DestinationFeature;
  /** A destination id (`page:…` / `soc:…`), or '' for none. */
  value: string;
  onChange: (destinationId: string) => void;
  /** Text for the "none chosen" state. Also the "all" row unless `required`. */
  allLabel: string;
  /** No "all" row: the screen needs one destination before it shows anything. */
  required?: boolean;
  id?: string;
}) {
  const { t, network, reason, kind, unsupported } = useLabels();
  const { view, all, query } = useDestinations(props.feature);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const root = useRef<HTMLDivElement>(null);
  const listId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onDown = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const current = all.find((dest) => dest.id === props.value);
  const label = current ? withChannel(t, current.network, current.name, current.networkName) : props.allLabel;
  const showSearch = view.total > SEARCH_FROM;

  const choose = (id: string) => {
    props.onChange(id);
    setOpen(false);
    setSearch('');
  };

  const unavailableCount = view.groups.reduce((sum, group) => sum + group.unavailable.length, 0);

  return (
    <div className="dest-picker" ref={root}>
      <button
        type="button"
        id={props.id}
        className={current ? 'dest-trigger' : 'dest-trigger placeholder'}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="dest-trigger-text">{label}</span>
        <span aria-hidden="true" className="dest-caret" />
      </button>

      {open && (
        <>
          <div className="dest-backdrop" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="dest-sheet" role="dialog" aria-label={props.allLabel}>
            <div className="dest-sheet-head">
              <strong>{props.allLabel}</strong>
              <button type="button" className="ghost small" onClick={() => setOpen(false)}>
                {t('dest.close')}
              </button>
            </div>

            {showSearch && (
              <input
                type="search"
                className="dest-search"
                placeholder={t('dest.search')}
                aria-label={t('dest.search')}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                autoFocus
              />
            )}

            <div className="dest-list" role="listbox" id={listId}>
              {!props.required && (
                <button
                  type="button"
                  role="option"
                  aria-selected={props.value === ''}
                  className={props.value === '' ? 'dest-option selected' : 'dest-option'}
                  onClick={() => choose('')}
                >
                  {props.allLabel}
                </button>
              )}

              {query.isLoading && <p className="muted small dest-note">{t('common.loading')}</p>}

              {view.groups.map((group) => {
                const usable = group.usable.filter((entry) => matches(entry.dest, search));
                if (usable.length === 0) return null;
                return (
                  <div key={group.network} role="group" aria-label={network(group.network, (group.usable[0] ?? group.unavailable[0])?.dest.networkName)}>
                    <div className="dest-group-head">
                      {network(group.network, (group.usable[0] ?? group.unavailable[0])?.dest.networkName)} <span className="muted">({usable.length})</span>
                    </div>
                    {usable.map((entry) => (
                      <button
                        type="button"
                        role="option"
                        key={entry.dest.id}
                        aria-selected={entry.dest.id === props.value}
                        className={
                          entry.dest.id === props.value ? 'dest-option selected' : 'dest-option'
                        }
                        onClick={() => choose(entry.dest.id)}
                      >
                        <span className="dest-name">{entry.dest.name}</span>
                        {kind(entry.dest) && <span className="dest-kind">{kind(entry.dest)}</span>}
                      </button>
                    ))}
                  </div>
                );
              })}

              {unavailableCount > 0 && (
                <details className="dest-unavailable">
                  <summary>{t('dest.unavailable', { count: unavailableCount })}</summary>
                  {view.groups.flatMap((group) =>
                    group.unavailable
                      .filter((entry) => matches(entry.dest, search))
                      .map((entry) => (
                        <div key={entry.dest.id} className="dest-option disabled" aria-disabled="true">
                          <span className="dest-name">
                            {withChannel(t, entry.dest.network, entry.dest.name, entry.dest.networkName)}
                          </span>
                          <span className="dest-kind warn">{reason(entry)}</span>
                        </div>
                      )),
                  )}
                </details>
              )}

              {view.total === 0 && !query.isLoading && (
                <p className="muted small dest-note">{t('dest.none')}</p>
              )}
            </div>

            {view.unsupported.length > 0 && (
              <p className="tiny muted dest-note">{unsupported(view.unsupported)}</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Pick any number of destinations to send something to.
 *
 * An entry that cannot be used stays in the list, greyed, with the reason. The
 * cost of a wrong send is higher than the cost of a longer list.
 */
export function DestinationChecklist(props: {
  feature: DestinationFeature;
  selected: string[];
  onChange: (destinationIds: string[]) => void;
  /** Shown but not choosable, e.g. before there is anything to send. */
  locked?: boolean;
}) {
  const { t, network, reason, kind, unsupported } = useLabels();
  const { view, query } = useDestinations(props.feature);
  const [search, setSearch] = useState('');
  /** Groups the person opened or closed by hand; the rest follow the default. */
  const [toggled, setToggled] = useState<Record<string, boolean>>({});
  const selected = useMemo(() => new Set(props.selected), [props.selected]);

  const toggle = (id: string, on: boolean) =>
    props.onChange(on ? [...props.selected, id] : props.selected.filter((value) => value !== id));

  const setGroup = (entries: DestinationEntry[], on: boolean) => {
    const ids = new Set(entries.map((entry) => entry.dest.id));
    const rest = props.selected.filter((id) => !ids.has(id));
    props.onChange(on ? [...rest, ...ids] : rest);
  };

  if (query.isLoading) return <div className="loading">{t('common.loading')}</div>;
  if (view.total === 0) return <p className="muted small">{t('dest.none')}</p>;

  return (
    <div className="dest-checklist">
      <div className="dest-checklist-bar">
        {view.total > SEARCH_FROM && (
          <input
            type="search"
            className="dest-search"
            placeholder={t('dest.search')}
            aria-label={t('dest.search')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        )}
        <span className="muted small">{t('dest.selected', { count: props.selected.length })}</span>
      </div>

      {view.groups.map((group, index) => {
        const usable = group.usable.filter((entry) => matches(entry.dest, search));
        const unavailable = group.unavailable.filter((entry) => matches(entry.dest, search));
        if (usable.length + unavailable.length === 0) return null;

        const chosen = usable.filter((entry) => selected.has(entry.dest.id)).length;
        const allOn = usable.length > 0 && chosen === usable.length;
        /*
         * A short list is all open. A long one opens its first group and any
         * group with something chosen, and the rest wait behind their header:
         * twenty entries on a phone is a scroll, not a choice. Searching opens
         * everything, since a match hidden in a closed group reads as no match.
         */
        const startOpen = view.total <= SEARCH_FROM || index === 0 || chosen > 0;
        const open = search.trim() !== '' || (toggled[group.network] ?? startOpen);

        const row = (entry: DestinationEntry) => (
          <li key={entry.dest.id} className={entry.cap.ok ? undefined : 'dim'}>
            <label>
              <input
                type="checkbox"
                disabled={!entry.cap.ok || props.locked}
                checked={selected.has(entry.dest.id)}
                onChange={(event) => toggle(entry.dest.id, event.target.checked)}
              />
              <span>{entry.dest.name}</span>
              {kind(entry.dest) && <span className="muted tiny dest-inline">{kind(entry.dest)}</span>}
              {!entry.cap.ok && <span className="warn tiny dest-inline">{reason(entry)}</span>}
            </label>
          </li>
        );

        return (
          <fieldset key={group.network} className="dest-group">
            <button
              type="button"
              className="dest-group-toggle"
              aria-expanded={open}
              onClick={() => setToggled((state) => ({ ...state, [group.network]: !open }))}
            >
              <span>
                {network(group.network, (group.usable[0] ?? group.unavailable[0])?.dest.networkName)} <span className="muted">({usable.length})</span>
                {chosen > 0 && <span className="badge ok tiny dest-inline">{chosen}</span>}
              </span>
              <span aria-hidden="true" className={open ? 'dest-caret open' : 'dest-caret'} />
            </button>

            {open && (
              <>
                {usable.length > 1 && !props.locked && (
                  <button
                    type="button"
                    className="ghost small"
                    onClick={() => setGroup(usable, !allOn)}
                  >
                    {allOn ? t('dest.clearAll') : t('dest.selectAll')}
                  </button>
                )}
                {usable.length > 0 && <ul className="target-list">{usable.map(row)}</ul>}
                {unavailable.length > 0 && (
                  <details className="dest-unavailable">
                    <summary>{t('dest.unavailable', { count: unavailable.length })}</summary>
                    <ul className="target-list">{unavailable.map(row)}</ul>
                  </details>
                )}
              </>
            )}
          </fieldset>
        );
      })}

      {view.unsupported.length > 0 && (
        <p className="tiny muted">{unsupported(view.unsupported)}</p>
      )}
    </div>
  );
}

/**
 * The same control for a short fixed list of choices (a channel, a status), so
 * it looks and behaves like the destination filter instead of a native select.
 */
export function ChoiceFilter(props: {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  allLabel: string;
  id?: string;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const listId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onDown = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const current = props.options.find((option) => option.value === props.value);
  const choose = (value: string) => {
    props.onChange(value);
    setOpen(false);
  };

  return (
    <div className="dest-picker" ref={root}>
      <button
        type="button"
        id={props.id}
        className={current ? 'dest-trigger' : 'dest-trigger placeholder'}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="dest-trigger-text">{current?.label ?? props.allLabel}</span>
        <span aria-hidden="true" className="dest-caret" />
      </button>

      {open && (
        <>
          <div className="dest-backdrop" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="dest-sheet" role="dialog" aria-label={props.allLabel}>
            <div className="dest-sheet-head">
              <strong>{props.allLabel}</strong>
              <button type="button" className="ghost small" onClick={() => setOpen(false)}>
                {t('dest.close')}
              </button>
            </div>
            <div className="dest-list" role="listbox" id={listId}>
              {[{ value: '', label: props.allLabel }, ...props.options].map((option) => (
                <button
                  type="button"
                  role="option"
                  key={option.value || 'all'}
                  aria-selected={option.value === props.value}
                  className={option.value === props.value ? 'dest-option selected' : 'dest-option'}
                  onClick={() => choose(option.value)}
                >
                  <span className="dest-name">{option.label}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
